import { inspectionRecordService } from '../services/inspection/inspectionRecordService';
import { InspectDatabase } from './database.structure';
import {
  EquipmentModel,
  EquipmentPartModel,
  PartLocationModel,
  PartModel,
  PartTypeModel,
} from './types';

export interface EquipmentPartJoinModel {
  equipmentPart: EquipmentPartModel;
  equipment: EquipmentModel | undefined;
  part: PartModel | undefined;
  partLocation: PartLocationModel | undefined;
  partSecondaryLocation: PartLocationModel | undefined;
  partGroup: number | undefined;
}

export default class DatabaseEquipmentPartsMethods {
  private db: InspectDatabase;

  constructor(db: InspectDatabase) {
    this.db = db;
  }

  public async searchEquipmentPartTypes({
    searchValue,
    partTypeName,
    partTypeId,
  }: {
    searchValue: string;
    partTypeName: string;
    partTypeId: number;
  }): Promise<PartModel[] | null> {
    const search = searchValue.toLowerCase();
    const partTypes = await this.db.parts
      .where('partTypeId')
      .equals(partTypeId)
      .filter(
        (part) =>
          part.name.toLowerCase().includes(search) ||
          part.partNumber.toLowerCase().includes(search),
      )
      .toArray();

    if (!partTypes.length) {
      return [];
    }

    return partTypes;
  }

  public async getEquipmentPart({
    equipmentPartId,
  }: {
    equipmentPartId: number;
  }): Promise<EquipmentPartJoinModel | null> {
    const equipmentPart = await this.db.equipmentParts
      .where('id')
      .equals(equipmentPartId)
      .first();

    if (!equipmentPart) {
      return null;
    }

    // Fetch the related equipment, part, and part location
    const equipment = await this.db.equipments.get(
      equipmentPart.externalEquipmentId,
    );

    if (!equipment) {
      return null;
    }

    const part = await this.db.parts
      .where('id')
      .equals(equipmentPart.partId)
      .first();
    const partType = part
      ? await this.db.partTypes.get(part.partTypeId)
      : undefined;
    const partCategory = partType
      ? await this.db.partCategories.get(partType.categoryId)
      : undefined;

    return Promise.all([
      this.db.equipments.get(equipment.externalId),
      this.db.partLocations.get(equipmentPart.locationId),
      this.db.partLocations.get(equipmentPart.secondaryLocationId),
      this.db.equipmentTypes.get(equipment.equipmentTypeId),
    ]).then(([equipment, partLocation, secondaryLocation, equipmentType]) => {
      return {
        equipmentPart: equipmentPart,
        equipment: {
          ...equipment,
          equipmentType: equipmentType?.name ?? 'Unknown',
        } as EquipmentModel,
        part: {
          ...part,
          partCategory: partCategory?.name ?? 'Unknown',
          partType: partType?.name ?? 'Unknown',
        } as PartModel,
        partLocation: partLocation,
        partSecondaryLocation: secondaryLocation,
        partGroup: equipmentPart.group,
      };
    });
  }

  public async equipmentPartToEquipmentPartJoinModel(
    equipmentPart: EquipmentPartModel,
  ): Promise<EquipmentPartJoinModel | null> {
    if (!equipmentPart) {
      return null;
    }

    const equipment = await this.db.equipments.get(
      equipmentPart.externalEquipmentId,
    );
    if (!equipment) {
      return null;
    }
    // Synced equipment rows carry only equipmentTypeId, and the condition-results heading
    // and part name read the type name.
    const equipmentType = await this.db.equipmentTypes.get(
      equipment.equipmentTypeId,
    );

    const part = await this.db.parts
      .where('id')
      .equals(equipmentPart.partId)
      .first();
    const partLocation = await this.db.partLocations.get(
      equipmentPart.locationId,
    );
    const secondaryLocation = await this.db.partLocations.get(
      equipmentPart.secondaryLocationId,
    );
    const partType = part
      ? await this.db.partTypes.get(part?.partTypeId)
      : undefined;
    const partCategory =
      partType !== undefined
        ? await this.db.partCategories.get(partType.categoryId)
        : part !== undefined
          ? await this.db.partCategories.get(part.partCategoryId)
          : undefined;

    if (part !== undefined) {
      part.partCategory = partCategory?.name || 'Unknown';
      part.partType = partType?.name || 'Unknown';
    }

    return {
      equipmentPart: equipmentPart,
      equipment: {
        ...equipment,
        equipmentType:
          equipmentType?.name ?? equipment.equipmentType ?? 'Unknown',
      },
      part: part,
      partLocation: partLocation,
      partSecondaryLocation: secondaryLocation,
      partGroup: equipmentPart.group,
    };
  }

  public async getEquipmentParts({
    equipmentPartIds,
  }: {
    equipmentPartIds: string[];
  }): Promise<EquipmentPartJoinModel[]> {
    const equipmentParts = await this.db.equipmentParts
      .where('externalId')
      .anyOf(equipmentPartIds)
      .toArray();
    if (!equipmentParts.length) {
      return [];
    }

    // Fetch the related equipment, part, and part location
    const equipments = await this.db.equipments
      .where('externalId')
      .anyOf(
        equipmentParts.map(
          (equipmentPart) => equipmentPart.externalEquipmentId,
        ),
      )
      .toArray();

    if (equipments.length === 0) {
      return [];
    }

    return Promise.all(
      equipmentParts.map((equipmentPart) => {
        const equipment = equipments.find(
          (equipment) =>
            equipment.externalId === equipmentPart.externalEquipmentId,
        );
        if (!equipment) {
          return null;
        }
        return Promise.all([
          this.db.equipments.get(equipment.externalId),
          this.db.parts.where('id').equals(equipmentPart.partId).first(),
          this.db.partLocations.get(equipmentPart.locationId),
          this.db.partLocations.get(equipmentPart.secondaryLocationId),
        ]).then(([equipment, part, partLocation, secondaryLocation]) => {
          return {
            equipmentPart: equipmentPart,
            equipment: equipment,
            part: part,
            partLocation: partLocation,
            partSecondaryLocation: secondaryLocation,
            partGroup: equipmentPart.group,
          };
        });
      }),
    ).then((results) => {
      return results.filter(
        (result) => result !== null,
      ) as EquipmentPartJoinModel[];
    });
  }

  public async getEquipmentEquipmentParts({
    equipmentId,
  }: {
    equipmentId: string;
  }): Promise<EquipmentPartJoinModel[]> {
    const equipment = await this.db.equipments.get(equipmentId);
    if (!equipment) {
      return [];
    }

    const equipmentParts = await this.db.equipmentParts
      .where('externalId')
      .anyOf(
        equipment.equipmentParts.map(
          (equipmentPart) => equipmentPart.externalId,
        ),
      )
      .toArray();
    if (!equipmentParts.length) {
      return [];
    }

    return Promise.all(
      equipmentParts.map((equipmentPart) => {
        return Promise.all([
          this.db.equipments.get(equipment.externalId),
          this.db.parts.where('id').equals(equipmentPart.partId).first(),
          this.db.partLocations.get(equipmentPart.locationId),
          this.db.partLocations.get(equipmentPart.secondaryLocationId),
        ]).then(([equipment, part, partLocation, secondaryLocation]) => {
          return {
            equipmentPart: equipmentPart,
            equipment: equipment,
            part: part,
            partLocation: partLocation,
            partSecondaryLocation: secondaryLocation,
            partGroup: equipmentPart.group,
          };
        });
      }),
    ).then((results) => {
      return results.filter(
        (result) => result !== null,
      ) as EquipmentPartJoinModel[];
    });
  }

  public async replaceEquipmentEquipmentParts({
    equipmentExternalId,
    equipmentParts,
  }: {
    equipmentExternalId: string;
    equipmentParts: EquipmentPartModel[];
  }): Promise<void> {
    await this.db.transaction(
      'rw',
      this.db.equipmentParts,
      this.db.inspections,
      this.db.inspectionBackups,
      async () => {
        const previous = await this.db.equipmentParts
          .where('externalEquipmentId')
          .equals(equipmentExternalId)
          .toArray();
        await inspectionRecordService.retainObservedFitments(previous);

        await this.db.equipmentParts
          .where('externalEquipmentId')
          .equals(equipmentExternalId)
          .delete();

        const newEquipmentParts = equipmentParts.map((ep) => ({
          ...ep,
          externalEquipmentId: equipmentExternalId,
        }));

        await this.db.equipmentParts.bulkAdd(newEquipmentParts);
      },
    );
  }
}
